import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Check,
  CloudUploadOutlined,
  Delete,
  Edit,
  FileCopy,
  ImageOutlined,
  Publish,
  Save,
} from '@mui/icons-material';

import { DetailPageTemplate } from '../../components/page-templates/detail-page/DetailPageTemplate';
import BackButton from '../../components/shared/actions/BackButton';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../components/shared/dialogs/ConfirmationDialog';
import { PictureTile } from '../../components/shared/tiles/PictureTile';
import { UserContext } from '../../components/shared/useUser';
import { Trash01 } from '../../assets';

import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';

import {
  RecipeKind,
  RecipeStatus,
  RecipeV2Detail,
  RecipeV2Tab,
} from './types';

import useRecipeV2Api from './hooks/useRecipeV2Api';
import useRecipeV2Detail from './hooks/useRecipeV2Detail';

import IngredientsTab from './components/IngredientsTab';
import MethodTab from './components/MethodTab';
import OverviewTab from './components/OverviewTab';
import CostingTab from './components/CostingTab';
import AllergensTab from './components/AllergensTab';
import DietaryTab from './components/DietaryTab';
import NutritionTab from './components/NutritionTab';
import VersionsTab from './components/VersionsTab';
import UsageTab from './components/UsageTab';

import { getRecipeSummaryInsights } from './components/RecipeSummaryTiles';
import { prepareRecipeImage } from './recipeMediaUpload';

const statusColor: Record<
    RecipeStatus,
    'success' | 'warning' | 'info' | 'default' | 'error'
> = {
  [RecipeStatus.Draft]: 'default',
  [RecipeStatus.Scheduled]: 'info',
  [RecipeStatus.Active]: 'success',
  [RecipeStatus.Expired]: 'error',
  [RecipeStatus.Archived]: 'warning',
};

const RecipeV2DetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('recipeV2');
  const theme = useTheme();

  const { hasPermissionTo, franchises } = useContext(UserContext);

  const {
    recipe,
    isLoading,
    error,
    loadRecipe,
    setRecipe,
  } = useRecipeV2Detail();

  const {
    currencyCode,
    getCurrencyCodeConfiguration,
  } = useCurrencyCodeConfiguration();

  const api = useRecipeV2Api();

  const recipeId = Number(id);

  const [tabValue, setTabValue] = useState<RecipeV2Tab>(
    RecipeV2Tab.Overview,
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [feedback, setFeedback] = useState<{
        message: string;
        severity: 'success' | 'error';
    } | null>(null);

  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneItemNumber, setCloneItemNumber] = useState('');
  const [cloneDescription, setCloneDescription] = useState('');
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [heroMediaUrl, setHeroMediaUrl] = useState<string | null>(null);
  const [isHeroMediaBusy, setIsHeroMediaBusy] = useState(false);
  const heroImageInputRef = useRef<HTMLInputElement | null>(null);

  const handleMetadataChange = (
    partial: Partial<RecipeV2Detail>,
  ) => {
    setRecipe((previous) =>
      previous
        ? {
          ...previous,
          ...partial,
        }
        : previous,
    );

    setIsDirty(true);
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    if (!Number.isNaN(recipeId)) {
      loadRecipe(recipeId);
    }
  }, [recipeId, loadRecipe]);

  useEffect(() => {
    if (!recipe?.id) {
      setHeroMediaUrl(null);
      return;
    }

    let active = true;

    api
      .getRecipeMediaUrl(recipe.id)
      .then((url) => {
        if (active) {
          setHeroMediaUrl(url);
        }
      })
      .catch(() => {
        if (active) {
          setMutationError('Recipe image could not be loaded.');
        }
      });

    return () => {
      active = false;
    };
  }, [api, recipe?.id]);

  const handleDelete = async () => {
    if (!recipe) return;

    setIsDeleting(true);

    try {
      const archived = await api.deleteRecipe(recipe.id);

      setRecipe((previous) =>
        previous
          ? {
            ...previous,
            ...archived,
          }
          : archived,
      );

      setFeedback({
        message: t('feedback.recipeArchived'),
        severity: 'success',
      });
    } catch (cause) {
      setFeedback({
        message:
                    cause instanceof Error
                      ? cause.message
                      : t('feedback.recipeArchiveError'),
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const openCloneDialog = () => {
    if (!recipe) return;

    setCloneItemNumber(`${recipe.itemNumber}-COPY`);
    setCloneDescription(`${recipe.description} (Copy)`);
    setCloneError(null);
    setCloneDialogOpen(true);
  };

  const handleClone = async () => {
    if (!recipe) return;

    setIsCloning(true);
    setCloneError(null);

    try {
      const clone = await api.cloneRecipe(recipe.id, {
        itemNumber: cloneItemNumber,
        description: cloneDescription,
      });

      setCloneDialogOpen(false);

      navigate(`/range/recipes/${clone.id}`);
    } catch (cause) {
      setCloneError(
        cause instanceof Error
          ? cause.message
          : 'Failed to clone recipe.',
      );
    } finally {
      setIsCloning(false);
    }
  };

  const buildUpdatePayload = (overrides?: Partial<RecipeV2Detail>) => {
    const source = overrides ? { ...recipe!, ...overrides } : recipe!;

    return {
      description: source.description,
      primaryCategory: source.primaryCategory,
      narrativeDescription: source.narrativeDescription,
      effectiveFrom: source.effectiveFrom,
      effectiveTo: source.effectiveTo,
      productionMethodId: source.productionMethodId,
      yieldPortions: source.yieldPortions,
      portionQuantity: source.portionQuantity,
      portionUom: source.portionUom,
      expectedYieldLossPercent: source.expectedYieldLossPercent,
      consumptionMode: source.consumptionMode ?? 'PerPortion',
      productionTrigger: source.productionTrigger,
      storageMethod: source.storageMethod,
      shelfLifeValue: source.shelfLifeValue,
      shelfLifeUnit: source.shelfLifeUnit,
    };
  };

  const saveMetadata = async () => {
    if (!recipe) return false;

    setMutationError(null);

    try {
      await api.updateRecipe(recipe.id, {
        description: recipe.description,
        primaryCategory: recipe.primaryCategory,
        narrativeDescription: recipe.narrativeDescription,
        effectiveFrom: recipe.effectiveFrom,
        effectiveTo: recipe.effectiveTo,
        productionMethodId: recipe.productionMethodId,
        yieldPortions: recipe.yieldPortions,
        portionQuantity: recipe.portionQuantity,
        portionUom: recipe.portionUom,
        expectedYieldLossPercent: recipe.expectedYieldLossPercent,
        consumptionMode: recipe.consumptionMode ?? 'PerPortion',
        productionTrigger: recipe.productionTrigger,
        storageMethod: recipe.storageMethod,
        shelfLifeValue: recipe.shelfLifeValue,
        shelfLifeUnit: recipe.shelfLifeUnit,
      });

      setIsDirty(false);

      return true;
    } catch (cause) {
      setMutationError(
        cause instanceof Error
          ? cause.message
          : 'Failed to save recipe.',
      );

      return false;
    }
  };

  // Narrative description saves immediately on confirm (rather than waiting on the
  // page-level Save button + isDirty flow other metadata edits use) so the Overview
  // tab's inline edit gives its own direct success/error feedback.
  const saveNarrativeDescription = async (value: string) => {
    if (!recipe) return;

    try {
      await api.updateRecipe(recipe.id, buildUpdatePayload({ narrativeDescription: value }));

      setRecipe((previous) =>
        previous
          ? {
            ...previous,
            narrativeDescription: value,
          }
          : previous,
      );

      setFeedback({
        message: t('feedback.recipeSaved', 'Recipe saved successfully'),
        severity: 'success',
      });
    } catch (cause) {
      setFeedback({
        message:
                    cause instanceof Error
                      ? cause.message
                      : 'Failed to save recipe.',
        severity: 'error',
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      if (await saveMetadata()) {
        await loadRecipe(recipeId);
        setFeedback({ message: t('feedback.recipeSaved', 'Recipe saved successfully'), severity: 'success' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!recipe) return;

    setIsPublishing(true);
    setMutationError(null);

    try {
      if (isDirty) {
        setIsSaving(true);

        const saved = await saveMetadata();

        setIsSaving(false);

        if (!saved) return;
      }

      await api.finishRecipe(recipe.id);
      await loadRecipe(recipeId);
    } catch (cause) {
      setMutationError(
        cause instanceof Error
          ? cause.message
          : 'Failed to publish recipe.',
      );
    } finally {
      setIsSaving(false);
      setIsPublishing(false);
    }
  };

  const uploadHeroMedia = async (file: File) => {
    if (!recipe) return;

    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp',
      ].includes(file.type)
    ) {
      setMutationError(
        'Select a JPEG, PNG, or WebP image.',
      );

      return;
    }

    setIsHeroMediaBusy(true);
    setMutationError(null);

    try {
      const prepared = await prepareRecipeImage(file);

      const upload = await api.authorizeRecipeMediaUpload(
        recipe.id,
        prepared,
      );

      const response = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': prepared.type,
        },
        body: prepared,
      });

      if (!response.ok) {
        throw new Error(
          'Image upload to storage failed.',
        );
      }

      await api.finalizeRecipeMediaUpload(
        recipe.id,
        upload.uploadId,
      );

      const mediaUrl = await api.getRecipeMediaUrl(
        recipe.id,
      );

      setHeroMediaUrl(mediaUrl);
    } catch (cause) {
      setMutationError(
        cause instanceof Error
          ? cause.message
          : 'Failed to upload recipe image.',
      );
    } finally {
      setIsHeroMediaBusy(false);
    }
  };

  const removeHeroMedia = async () => {
    if (!recipe) return;

    setIsHeroMediaBusy(true);
    setMutationError(null);

    try {
      await api.removeRecipeMedia(recipe.id);
      setHeroMediaUrl(null);
    } catch (cause) {
      setMutationError(
        cause instanceof Error
          ? cause.message
          : 'Failed to remove recipe image.',
      );
    } finally {
      setIsHeroMediaBusy(false);
    }
  };

  const hasWritePermission =
        !isLoading && recipe
          ? hasPermissionTo(['RecipeV2Write'])
          : false;

  const hasDeletePermission =
        !isLoading && recipe
          ? hasPermissionTo(['RecipeV2Delete'])
          : false;

  const hasCostingPermission =
        hasPermissionTo(['RecipeV2CostingRead']);

  const hasAllergenPermission =
        hasPermissionTo(['RecipeV2Read']);

  const hasVersionPermission =
        hasPermissionTo(['RecipeV2Read']);

  const hasApprovePermission =
        hasPermissionTo(['RecipeV2Approve']);

  const hasRollbackPermission =
        hasPermissionTo(['RecipeV2VersionRollback']);

  const franchiseName = recipe
    ? (
      franchises.find(
        (franchise) =>
          franchise.franchiseId === recipe.franchiseId,
      )?.franchiseName
            ?? `Franchise ${recipe.franchiseId}`
    )
    : '';

  const refreshGovernanceState = async () => {
    await loadRecipe(recipeId);
  };

  const tabs = useMemo(() => {
    if (!recipe) return [];

    const items = [
      {
        id: RecipeV2Tab.Overview,
        label: t('tabs.overview', 'Overview'),
        content: (
          <OverviewTab
            recipe={recipe}
            editable={hasWritePermission}
            onSaveNarrative={saveNarrativeDescription}
          />
        ),
      },
      {
        id: RecipeV2Tab.Ingredients,
        label: t('tabs.ingredients'),
        content: (
          <IngredientsTab
            recipe={recipe}
            editable={hasWritePermission}
            currencyCode={currencyCode}
            onMetadataChange={handleMetadataChange}
          />
        ),
      },

      // #1008: method/steps support.
      {
        id: RecipeV2Tab.Method,
        label: t('tabs.method'),
        content: (
          <MethodTab
            recipe={recipe}
            editable={hasWritePermission}
            onMetadataChange={handleMetadataChange}
          />
        ),
      },
    ];

    if (hasCostingPermission) {
      items.push({
        id: RecipeV2Tab.Costing,
        label: t('tabs.costing'),
        content: (
          <CostingTab
            recipe={recipe}
            currencyCode={currencyCode}
          />
        ),
      });
    }

    if (hasAllergenPermission) {
      items.push({
        id: RecipeV2Tab.Allergens,
        label: t('tabs.allergens'),
        content: (
          <AllergensTab
            recipe={recipe}
            onMetadataChange={handleMetadataChange}
          />
        ),
      });

      items.push({
        id: RecipeV2Tab.Dietary,
        label: t('tabs.dietary'),
        content: (
          <DietaryTab
            recipe={recipe}
            onMetadataChange={handleMetadataChange}
          />
        ),
      });
    }

    items.push({
      id: RecipeV2Tab.Nutrition,
      label: t('tabs.nutrition'),
      content: (
        <NutritionTab
          recipe={recipe}
          onMetadataChange={handleMetadataChange}
        />
      ),
    });

    if (hasVersionPermission) {
      items.push({
        id: RecipeV2Tab.Versions,
        label: t('tabs.versions'),
        content: (
          <VersionsTab
            recipe={recipe}
            canWrite={hasWritePermission}
            canApprove={hasApprovePermission}
            canRollback={hasRollbackPermission}
            onGovernanceMutated={refreshGovernanceState}
          />
        ),
      });
    }

    items.push({
      id: RecipeV2Tab.Usage,
      label: t('tabs.usage', 'Usage'),
      content: (
        <UsageTab recipeId={recipe.id} />
      ),
    });

    return items;

    // The metadata callback deliberately mutates the loaded recipe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recipe,
    hasWritePermission,
    hasApprovePermission,
    hasRollbackPermission,
    hasCostingPermission,
    hasAllergenPermission,
    hasVersionPermission,
    currencyCode,
    t,
  ]);

  const recipePicture = recipe ? (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
      }}
    >
      <PictureTile
        src={heroMediaUrl}
        alt={`${recipe.description} recipe image`}
        loading={isHeroMediaBusy}
        objectFit="cover"
        fallbackLabel="No recipe image"
        minHeight={0}
        sx={{ height: '100%' }}
        fallback={(
          <Stack
            alignItems="center"
            spacing={1}
          >
            <ImageOutlined
              sx={{
                fontSize: 40,
              }}
            />

            <Typography variant="textSM">
                            No recipe image
            </Typography>
          </Stack>
        )}
      />

      <input
        ref={heroImageInputRef}
        type="file"
        aria-label="Change recipe image"
        accept="image/jpeg,image/png,image/webp"
        disabled={isHeroMediaBusy}
        style={{
          display: 'none',
        }}
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) {
            uploadHeroMedia(file);
          }

          event.currentTarget.value = '';
        }}
      />

      {hasWritePermission && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
          }}
        >
          <IconButton
            size="small"
            aria-label={
              heroMediaUrl
                ? 'Change recipe image'
                : 'Add recipe image'
            }
            disabled={isHeroMediaBusy}
            onClick={() =>
              heroImageInputRef.current?.click()
            }
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                bgcolor: 'common.white',
              },
            }}
          >
            <CloudUploadOutlined fontSize="small" />
          </IconButton>

          {heroMediaUrl && (
            <IconButton
              size="small"
              aria-label="Remove recipe image"
              color="error"
              disabled={isHeroMediaBusy}
              onClick={removeHeroMedia}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'common.white',
                },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}
    </Box>
  ) : (
    <Box />
  );

  return (
    <DetailPageTemplate
      title={recipe?.description ?? ''}
      subtitle={
        recipe
          ? (
            <Stack spacing={0.5}>
              <Typography
                color={theme.palette.custom.gray[600]}
                variant="textMD"
              >
                {`${recipe.itemNumber} · ${recipe.primaryCategory || '—'
                }`}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mt: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={
                    recipe.status
                      ? t(
                        `status.${recipe.status
                          .charAt(0)
                          .toLowerCase()
                        }${recipe.status.slice(1)}`,
                      )
                      : 'Unknown'
                  }
                  color={
                    statusColor[recipe.status]
                                        ?? 'default'
                  }
                  size="small"
                />

                {recipe.recipeType
                                    === RecipeKind.Batch && (
                  <Chip
                    label={t('batch.badge')}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}

                <Chip
                  label={`v${recipe.currentVersion ?? 1
                  }`}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              {recipe.narrativeDescription && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  <Typography
                    color={theme.palette.custom.gray[700]}
                    variant="textSM"
                    sx={{ fontStyle: 'italic' }}
                  >
                    {recipe.narrativeDescription}
                  </Typography>
                </Box>
              )}
            </Stack>
          )
          : ''
      }
      loading={isLoading}
      headlineLayoutInSkeleton="picture-six"
      showPictureInSkeleton
      insightCountInSkeleton={6}
      backButton={(
        <BackButton
          title={t('actions.back')}
          isLink={false}
          handleClick={() =>
            navigate('/range/recipes')
          }
        />
      )}
      headlineContent={{
        // Keep #811's six summary tiles and add
        // #1008's hero image.
        layout: 'picture-six',
        picture: recipePicture,
        insights: recipe
          ? getRecipeSummaryInsights(
            recipe,
            currencyCode,
            t,
          )
          : ([
            <span key="1" />,
            <span key="2" />,
            <span key="3" />,
            <span key="4" />,
            <span key="5" />,
            <span key="6" />,
          ] as const),
      }}
      tabs={tabs}
      activeTabId={tabValue}
      onTabChange={(tabId) =>
        setTabValue(tabId as RecipeV2Tab)
      }
      actions={
        recipe
          ? (
            <>
              {hasWritePermission && isDirty && (
                <Button
                  size="md"
                  startIcon={<Save />}
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    flex: {
                      xs: 1,
                      sm: 'initial',
                    },
                    flexShrink: 0,
                  }}
                >
                  {t('actions.save')}
                </Button>
              )}

              {hasWritePermission
                                && recipe.status
                                === RecipeStatus.Draft && (
                <Button
                  size="md"
                  startIcon={<Publish />}
                  variant="primary"
                  onClick={handlePublish}
                  disabled={
                    isPublishing || isSaving
                  }
                  sx={{
                    flex: {
                      xs: 1,
                      sm: 'initial',
                    },
                    flexShrink: 0,
                  }}
                >
                  {isPublishing
                    ? 'Publishing…'
                    : 'Publish Recipe'}
                </Button>
              )}

              {hasWritePermission && (
                <Button
                  size="md"
                  startIcon={<FileCopy />}
                  variant="secondary"
                  onClick={openCloneDialog}
                  sx={{
                    flex: {
                      xs: 1,
                      sm: 'initial',
                    },
                    flexShrink: 0,
                  }}
                >
                                    Clone Recipe
                </Button>
              )}

              {hasDeletePermission && (
                <Button
                  size="md"
                  startIcon={<Delete />}
                  variant="secondary"
                  color="error"
                  onClick={() =>
                    setDeleteDialogOpen(true)
                  }
                  sx={{
                    flex: {
                      xs: 1,
                      sm: 'initial',
                    },
                    flexShrink: 0,
                  }}
                >
                  {t('actions.deleteConfirm')}
                </Button>
              )}
            </>
          )
          : undefined
      }
    >
      {error || !recipe
        ? (
          <Typography color="error">
            {error || t('notFound')}
          </Typography>
        )
        : (
          <>
            {mutationError && (
              <Typography
                color="error"
                sx={{
                  mb: 2,
                }}
              >
                {mutationError}
              </Typography>
            )}
          </>
        )}

      {recipe && (
        <>
          <ConfirmationDialog
            open={deleteDialogOpen}
            icon={<Trash01 />}
            variant="bad"
            title={t(
              'actions.deleteConfirmTitle',
            )}
            message={t(
              'actions.deleteConfirmMessage',
            )}
            confirmButtonText={
              isDeleting
                ? t('actions.deleting')
                : t('actions.deleteConfirm')
            }
            cancelButtonText={t(
              'actions.cancel',
            )}
            isProcessing={isDeleting}
            onConfirm={handleDelete}
            onCancel={() =>
              setDeleteDialogOpen(false)
            }
          />

          {feedback && (
            <MessagesSnackbar
              open={Boolean(feedback)}
              onClose={() =>
                setFeedback(null)
              }
              message={feedback.message}
              severity={feedback.severity}
              duration={4000}
            />
          )}

          <Dialog
            open={cloneDialogOpen}
            onClose={() => {
              if (!isCloning) {
                setCloneDialogOpen(false);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
                            Clone Recipe
            </DialogTitle>

            <DialogContent>
              {cloneError && (
                <Typography
                  color="error"
                  sx={{
                    mb: 2,
                  }}
                >
                  {cloneError}
                </Typography>
              )}

              <Stack
                spacing={0}
                sx={{
                  pt: 1,
                }}
              >
                <TextField
                  label="Recipe name"
                  value={cloneDescription}
                  onChange={(event) =>
                    setCloneDescription(
                      event.target.value,
                    )
                  }
                  required
                  fullWidth
                  sx={{
                    mb: '16px',
                  }}
                />

                <TextField
                  label="Item number / SKU"
                  value={cloneItemNumber}
                  onChange={(event) =>
                    setCloneItemNumber(
                      event.target.value,
                    )
                  }
                  required
                  fullWidth
                  sx={{
                    mb: '16px',
                  }}
                />

                <TextField
                  label="Franchise"
                  value={franchiseName}
                  InputProps={{
                    readOnly: true,
                  }}
                  fullWidth
                  sx={{
                    mb: '12px',
                  }}
                />
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() =>
                  setCloneDialogOpen(false)
                }
                variant="secondary"
                disabled={isCloning}
              >
                {t('actions.cancel')}
              </Button>

              <Button
                onClick={handleClone}
                variant="primary"
                disabled={
                  isCloning
                                    || !cloneItemNumber.trim()
                                    || !cloneDescription.trim()
                }
              >
                {isCloning
                  ? 'Cloning…'
                  : 'Clone Recipe'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </DetailPageTemplate>
  );
};

export default RecipeV2DetailPage;
